import { FormEvent, useId, useState } from "react";
import {
    MailboxTestResultBase,
    TestMailboxReceive,
    TestMailboxReceiveResult,
    TestMailboxSend,
    TestMailboxSendResult,
} from "./MailboxTestAPI";
import { isPlausibleEmail, TestOutcome, testOutcome } from "./mailboxTestOutcome";
import ResolvedSettingsDialog from "./ResolvedSettingsDialog";
import "./mailboxTests.css";

interface iMailboxTestsProps {
    /** The tenant token. Must carry sys-admin; the parent only renders this for one that does. */
    token: string;
    mailboxId: number;
    mailboxName: string;
    /**
     * The form holds edits that are not saved. Both tests run against the
     * SAVED settings (contract §3.5), so testing now would test something
     * other than what is on screen. The test buttons are disabled with the
     * reason beside them rather than silently testing the old values.
     */
    unsavedChanges: boolean;
}

function formatElapsed(ms: number): string {
    return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

/** Which server the test used. Absent when the test failed at `Resolve`, so the line may be empty. */
function connectionLine(result: MailboxTestResultBase): string {
    const target = result.server ? (result.port !== undefined ? `${result.server}:${result.port}` : result.server) : "";
    return [result.provider, target, formatElapsed(result.elapsedMs)].filter(Boolean).join(" · ");
}

/** The send test's addresses, when the server reported them. */
function addressLine(result: TestMailboxSendResult): string | undefined {
    if (result.from && result.recipient) {
        return `From ${result.from} to ${result.recipient}`;
    }
    if (result.from) {
        return `From ${result.from}`;
    }
    if (result.recipient) {
        return `To ${result.recipient}`;
    }
    return undefined;
}

/**
 * The outcome of one test call.
 *
 * `invalid` renders nothing here: a `400` belongs next to the recipient
 * field, which the send form draws itself.
 */
function TestResult<T extends MailboxTestResultBase>({
    outcome,
    extra,
}: {
    outcome: TestOutcome<T>;
    extra?: (result: T) => string | undefined;
}) {
    switch (outcome.kind) {
        case "passed":
        case "failed":
        case "busy": {
            const { result } = outcome;
            const detail = extra?.(result);
            const className =
                outcome.kind === "passed" ? "mbt-pass" : outcome.kind === "busy" ? "mbt-busy" : "mbt-fail";
            return (
                <div
                    role="status"
                    className={`mbt-result ${className}`}
                >
                    {outcome.kind === "passed" && (
                        <p>
                            <b>Passed.</b> {result.message}
                        </p>
                    )}
                    {outcome.kind === "busy" && (
                        <p>
                            <b>The mailbox is being processed now.</b> Try again shortly.
                        </p>
                    )}
                    {outcome.kind === "failed" && (
                        <>
                            <p>
                                <b>{outcome.label}.</b>
                            </p>
                            {result.error && <p className="mbt-error-text">{result.error}</p>}
                        </>
                    )}
                    {detail && <p className="mbt-detail">{detail}</p>}
                    <p className="mbt-detail">{connectionLine(result)}</p>
                </div>
            );
        }
        case "invalid":
            return null;
        case "forbidden":
            return (
                <div
                    role="status"
                    className="mbt-result mbt-fail"
                >
                    <p>
                        <b>Not authorised.</b> Mailbox tests need a sys-admin user.
                    </p>
                </div>
            );
        case "notFound":
            return (
                <div
                    role="status"
                    className="mbt-result mbt-fail"
                >
                    <p>
                        <b>Mailbox not found.</b> It may have been removed. Reload the list.
                    </p>
                </div>
            );
        case "unauthorised":
            return (
                <div
                    role="status"
                    className="mbt-result mbt-fail"
                >
                    <p>Your session has expired. Reload the page to sign in again.</p>
                </div>
            );
        case "network":
            return (
                <div
                    role="status"
                    className="mbt-result mbt-fail"
                >
                    <p>
                        <b>The request did not reach the server.</b> Nothing was tested. Try again.
                    </p>
                </div>
            );
        case "error":
            return (
                <div
                    role="status"
                    className="mbt-result mbt-fail"
                >
                    <p>
                        <b>The test could not be run.</b> The server returned status {outcome.status}.
                    </p>
                </div>
            );
    }
}

function receiveDetail(result: TestMailboxReceiveResult): string | undefined {
    if (result.messageCount === undefined) {
        return undefined;
    }
    return result.messageCount === 1 ? "1 message waiting" : `${result.messageCount} messages waiting`;
}

/**
 * Live connection tests for one mailbox, plus the resolved-settings popup.
 *
 * Nothing here changes the mailbox, and nothing appears in the mail
 * processing timeline (contract §3.5) — EXCEPT that the send test delivers a
 * real email. It is a separate, armed step for that reason: the first click
 * asks for a recipient, and only the second sends.
 *
 * Neither test retries itself. A receive test can wait out the processing
 * lock before it answers, so each button disables itself while its own call
 * runs and the admin decides whether to go again.
 */
function MailboxTests({ token, mailboxId, mailboxName, unsavedChanges }: iMailboxTestsProps) {
    const [receiving, setReceiving] = useState(false);
    const [receiveOutcome, setReceiveOutcome] = useState<TestOutcome<TestMailboxReceiveResult>>();

    const [sendArmed, setSendArmed] = useState(false);
    const [recipient, setRecipient] = useState("");
    const [recipientMessages, setRecipientMessages] = useState<string[]>([]);
    const [sending, setSending] = useState(false);
    const [sendOutcome, setSendOutcome] = useState<TestOutcome<TestMailboxSendResult>>();

    const [showSettings, setShowSettings] = useState(false);

    const recipientId = useId();
    const recipientErrorId = useId();

    async function runReceive() {
        setReceiving(true);
        setReceiveOutcome(undefined);
        try {
            setReceiveOutcome(testOutcome(await TestMailboxReceive(token, mailboxId)));
        } finally {
            setReceiving(false);
        }
    }

    async function runSend(event: FormEvent) {
        event.preventDefault();
        const address = recipient.trim();
        if (!isPlausibleEmail(address)) {
            setRecipientMessages(["Enter a valid email address."]);
            return;
        }

        setRecipientMessages([]);
        setSendOutcome(undefined);
        setSending(true);
        try {
            const outcome = testOutcome(await TestMailboxSend(token, mailboxId, address));
            if (outcome.kind === "invalid") {
                // The server decides. Its reason goes beside the field, and the
                // form stays open so the address can be corrected in place.
                setRecipientMessages(
                    outcome.messages.length > 0 ? outcome.messages : ["The server rejected this address."],
                );
            } else {
                setSendOutcome(outcome);
                setSendArmed(false);
            }
        } finally {
            setSending(false);
        }
    }

    const testsDisabled = unsavedChanges;

    return (
        <div className="mbt">
            {unsavedChanges && (
                <p className="mbt-note">
                    You have unsaved changes. The tests use the saved settings, so update the mailbox before testing.
                </p>
            )}

            <div className="mbt-row">
                <button
                    type="button"
                    className="g-btn btn-default"
                    disabled={testsDisabled || receiving}
                    aria-busy={receiving}
                    onClick={() => void runReceive()}
                >
                    {receiving ? "Testing receive…" : "Test receive"}
                </button>
                <button
                    type="button"
                    className="g-btn btn-default"
                    disabled={testsDisabled || sending || sendArmed}
                    onClick={() => {
                        setSendArmed(true);
                        setSendOutcome(undefined);
                        setRecipientMessages([]);
                    }}
                >
                    Send test email…
                </button>
                <button
                    type="button"
                    className="g-btn btn-default"
                    onClick={() => setShowSettings(true)}
                >
                    Show resolved settings
                </button>
            </div>

            {receiveOutcome && (
                <div className="mbt-section">
                    <div className="mbt-section-head">Receive test</div>
                    <TestResult
                        outcome={receiveOutcome}
                        extra={receiveDetail}
                    />
                </div>
            )}

            {sendArmed && (
                <form
                    className="mbt-confirm"
                    onSubmit={event => void runSend(event)}
                    noValidate
                >
                    <div className="mbt-section-head">Send a real test email</div>
                    <p className="mbt-detail">
                        One email is sent from this mailbox with the subject “EMS mailbox test – {mailboxName}”.
                    </p>
                    <div>
                        <label htmlFor={recipientId}>Recipient</label>
                        <input
                            id={recipientId}
                            type="email"
                            autoComplete="email"
                            autoFocus
                            value={recipient}
                            aria-invalid={recipientMessages.length > 0}
                            aria-describedby={recipientMessages.length > 0 ? recipientErrorId : undefined}
                            onChange={event => setRecipient(event.target.value)}
                        />
                        {recipientMessages.length > 0 && (
                            <div
                                id={recipientErrorId}
                                className="mbt-field-error"
                            >
                                {recipientMessages.map((message, index) => (
                                    <div key={`${index}-${message}`}>{message}</div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="mbt-row">
                        <button
                            type="submit"
                            className="g-btn btn-primary"
                            disabled={sending || testsDisabled}
                            aria-busy={sending}
                        >
                            {sending ? "Sending…" : "Send test email"}
                        </button>
                        <button
                            type="button"
                            className="g-btn btn-default"
                            disabled={sending}
                            onClick={() => {
                                setSendArmed(false);
                                setRecipientMessages([]);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            {sendOutcome && (
                <div className="mbt-section">
                    <div className="mbt-section-head">Send test</div>
                    <TestResult
                        outcome={sendOutcome}
                        extra={addressLine}
                    />
                </div>
            )}

            {showSettings && (
                <ResolvedSettingsDialog
                    token={token}
                    mailboxId={mailboxId}
                    mailboxName={mailboxName}
                    unsavedChanges={unsavedChanges}
                    onClose={() => setShowSettings(false)}
                />
            )}
        </div>
    );
}

export default MailboxTests;
